import { Link } from "react-router-dom";
import { MapPin, Globe, BarChart3, Target, ArrowRight } from "lucide-react";

const MapAlerts = () => {
  return (
    <div className="min-h-screen">
      {/* Hero Section */}
      <section className="bg-gradient-to-br from-green-50 via-white to-blue-50 py-20">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-16">
            <h1 className="text-5xl font-bold mb-6">
              <span className="gradient-text">MAP Alerts</span>
              <br />& Enforcement
            </h1>
            <p className="text-xl text-gray-600 max-w-3xl mx-auto">
              Monitor and enforce Minimum Advertised Price policies across all
              channels. Protect brand value and maintain pricing integrity.
            </p>
          </div>

          <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 items-center">
            <div>
              <div className="bg-white rounded-2xl shadow-xl p-8">
                <div className="flex items-center justify-between mb-6">
                  <h3 className="text-lg font-semibold">
                    MAP Violations Dashboard
                  </h3>
                  <div className="flex items-center space-x-2 text-green-600">
                    <Globe className="h-4 w-4" />
                    <span className="text-sm font-medium">50+ Countries</span>
                  </div>
                </div>
                <div className="space-y-4">
                  <div className="flex justify-between items-center p-3 bg-green-50 rounded-lg border-l-4 border-green-500">
                    <div className="flex items-center space-x-3">
                      <MapPin className="h-4 w-4 text-green-600" />
                      <span className="text-gray-700">North America</span>
                    </div>
                    <div className="text-right">
                      <div className="font-semibold text-green-600">
                        $129.99
                      </div>
                      <div className="text-xs text-gray-500">Compliant</div>
                    </div>
                  </div>
                  <div className="flex justify-between items-center p-3 bg-blue-50 rounded-lg border-l-4 border-blue-500">
                    <div className="flex items-center space-x-3">
                      <MapPin className="h-4 w-4 text-blue-600" />
                      <span className="text-gray-700">Europe</span>
                    </div>
                    <div className="text-right">
                      <div className="font-semibold text-red-600">€99.99</div>
                      <div className="text-xs text-gray-500">MAP Violation</div>
                    </div>
                  </div>
                  <div className="flex justify-between items-center p-3 bg-purple-50 rounded-lg border-l-4 border-purple-500">
                    <div className="flex items-center space-x-3">
                      <MapPin className="h-4 w-4 text-purple-600" />
                      <span className="text-gray-700">Asia Pacific</span>
                    </div>
                    <div className="text-right">
                      <div className="font-semibold text-green-600">¥899</div>
                      <div className="text-xs text-gray-500">Compliant</div>
                    </div>
                  </div>
                </div>
              </div>
            </div>
            <div className="space-y-6">
              <div className="flex items-start space-x-4">
                <div className="w-12 h-12 bg-gradient-to-r from-green-600 to-blue-600 rounded-lg flex items-center justify-center">
                  <Globe className="h-6 w-6 text-white" />
                </div>
                <div>
                  <h3 className="text-xl font-semibold mb-2">
                    MAP Policy Monitoring
                  </h3>
                  <p className="text-gray-600">
                    24/7 monitoring of advertised prices across all channels and
                    retailers.
                  </p>
                </div>
              </div>
              <div className="flex items-start space-x-4">
                <div className="w-12 h-12 bg-gradient-to-r from-blue-600 to-purple-600 rounded-lg flex items-center justify-center">
                  <BarChart3 className="h-6 w-6 text-white" />
                </div>
                <div>
                  <h3 className="text-xl font-semibold mb-2">
                    Violation Alerts
                  </h3>
                  <p className="text-gray-600">
                    Instant notifications when retailers violate your MAP
                    policies.
                  </p>
                </div>
              </div>
              <div className="flex items-start space-x-4">
                <div className="w-12 h-12 bg-gradient-to-r from-purple-600 to-pink-600 rounded-lg flex items-center justify-center">
                  <Target className="h-6 w-6 text-white" />
                </div>
                <div>
                  <h3 className="text-xl font-semibold mb-2">
                    Enforcement Tools
                  </h3>
                  <p className="text-gray-600">
                    Automated enforcement actions and retailer communication
                    tools.
                  </p>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Features Section */}
      <section className="py-20 bg-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-16">
            <h2 className="text-4xl font-bold mb-4">
              <span className="gradient-text">Map Solution Features</span>
            </h2>
            <p className="text-xl text-gray-600">
              Comprehensive geographic pricing intelligence
            </p>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8">
            {[
              {
                title: "Interactive Heat Maps",
                description:
                  "Visual representation of pricing opportunities across regions",
                icon: "🗺️",
              },
              {
                title: "Currency Optimization",
                description:
                  "Automatic currency conversion and local pricing optimization",
                icon: "💱",
              },
              {
                title: "Demographic Analysis",
                description:
                  "Understand local demographics and purchasing behavior",
                icon: "👥",
              },
              {
                title: "Competitor Mapping",
                description:
                  "Track competitor presence and pricing in each region",
                icon: "🎯",
              },
              {
                title: "Seasonal Patterns",
                description:
                  "Identify regional seasonal trends and adjust pricing accordingly",
                icon: "📅",
              },
              {
                title: "Local Regulations",
                description:
                  "Stay compliant with regional pricing laws and regulations",
                icon: "⚖️",
              },
            ].map((feature, index) => (
              <div key={index} className="card">
                <div className="text-4xl mb-4">{feature.icon}</div>
                <h3 className="text-xl font-semibold mb-3">{feature.title}</h3>
                <p className="text-gray-600">{feature.description}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Use Cases Section */}
      <section className="py-20 bg-gray-50">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-16">
            <h2 className="text-4xl font-bold mb-4">
              <span className="gradient-text">Use Cases</span>
            </h2>
            <p className="text-xl text-gray-600">
              How businesses leverage geographic pricing intelligence
            </p>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-8">
            <div className="card">
              <h3 className="text-xl font-semibold mb-4">
                E-commerce Expansion
              </h3>
              <p className="text-gray-600 mb-4">
                Optimize pricing for new markets before expansion. Understand
                local competition and price sensitivity to maximize market entry
                success.
              </p>
              <ul className="text-sm text-gray-500 space-y-1">
                <li>• Market entry pricing strategies</li>
                <li>• Competitive landscape analysis</li>
                <li>• Local demand assessment</li>
              </ul>
            </div>
            <div className="card">
              <h3 className="text-xl font-semibold mb-4">
                Multi-Region Optimization
              </h3>
              <p className="text-gray-600 mb-4">
                Balance pricing across multiple regions to maximize global
                revenue while maintaining competitive positioning in each
                market.
              </p>
              <ul className="text-sm text-gray-500 space-y-1">
                <li>• Cross-border price harmonization</li>
                <li>• Regional profit optimization</li>
                <li>• Currency risk management</li>
              </ul>
            </div>
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <section className="py-20 bg-gradient-to-r from-green-600 via-blue-600 to-purple-600">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <h2 className="text-4xl font-bold text-white mb-6">
            Expand Globally with Confidence
          </h2>
          <p className="text-xl text-green-100 mb-8">
            Make informed pricing decisions with comprehensive geographic
            intelligence
          </p>
          <div className="flex flex-col sm:flex-row gap-4 justify-center">
            <Link
              to="/trial"
              className="bg-white text-gray-900 px-8 py-4 rounded-lg font-semibold hover:bg-gray-100 transition-colors inline-flex items-center justify-center"
            >
              Start Free Trial <ArrowRight className="ml-2 h-5 w-5" />
            </Link>
            <Link
              to="/contact"
              className="border-2 border-white text-white px-8 py-4 rounded-lg font-semibold hover:bg-white hover:text-gray-900 transition-colors"
            >
              Schedule Demo
            </Link>
          </div>
        </div>
      </section>
    </div>
  );
};

export default MapAlerts;
